import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";

import {
  EMPTY_STRING,
  PAYLOAD_KEYS,
  propertyConstants,
  adminReduxConstants,
  CONFIRMATION_MESSAGE,
  PROPERTY_LIMIT_EXCEEDED_MESSAGE,
} from "../../../constants";
import { buildFilterQuery } from "../../../shared/utility";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import ConfirmationModal from "../../../shared/components/modals/ConfirmationModal";
import PropertyFilter from "../../../shared/components/property-filter/PropertyFilter";
import PropertyCardAdmin from "../../../shared/components/property-card/PropertyCardAdmin";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import {
  adminActions,
  systemActions,
  propertyActions,
} from "../../../redux/actions";

const { FILTER_QUERY, PAGE_SIZE, SEARCH, ORDER_BY } = PAYLOAD_KEYS;
const { ALL_PROPERTIES_PAGINATION } = propertyConstants;

function AdminProperties({
  propertyId,
  allCountries,
  allProperties,
  getAllProperties,
  allPropertiesPagination,
  updatePropertyStatus,
  spinnerActions,
  updatePagination,
  isConfirmationModal,
  setIsConfirmationModal,
  setSelectedRowId,
  selectedRowId,
}) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const getFilterQuery = (data, countryFilter) => {
    const searchFields = ["Title", "AgentName"];

    // Build the initial filter query from the data
    let filter = buildFilterQuery(data, searchFields);

    // Dynamically append the filters based on their presence
    if (countryFilter && countryFilter !== EMPTY_STRING) {
      filter =
        filter !== EMPTY_STRING
          ? `(${filter}) && (${countryFilter})`
          : countryFilter;
    }

    return filter;
  };

  const handleUpdatePagination = (data, type) => {
    const payload = {
      ...allPropertiesPagination,
      [SEARCH]: type === SEARCH ? data : allPropertiesPagination.search,
      [PAGE_SIZE]: type === SEARCH || type === ORDER_BY ? 10 : data,
      [ORDER_BY]: type === ORDER_BY ? data : allPropertiesPagination.orderBy,
    };
    updatePagination(payload, ALL_PROPERTIES_PAGINATION);
  };

  const handleChangeCountryFilter = (value) => {
    updatePagination(
      { ...allPropertiesPagination, country: value },
      ALL_PROPERTIES_PAGINATION
    );
  };

  const resetFilter = () => {
    updatePagination(
      {
        ...allPropertiesPagination,
        search: EMPTY_STRING,
        orderBy: EMPTY_STRING,
        filterQuery: EMPTY_STRING,
        country: EMPTY_STRING,
      },
      ALL_PROPERTIES_PAGINATION
    );
  };

  const getUpdatedPayload = () => {
    const updatedPayload = {
      ...allPropertiesPagination,
      [FILTER_QUERY]: getFilterQuery(
        allPropertiesPagination.search,
        allPropertiesPagination.country
      ),
    };

    return updatedPayload;
  };

  const updateStatus = (id) => {
    setSelectedRowId(id);
    setShowConfirmationModal(true);
  };

  const onConfirmation = () => {
    setShowConfirmationModal(false);
    updatePropertyStatus(selectedRowId, () =>
      getAllProperties(getUpdatedPayload())
    );
  };

  const onReConfirmation = () => {
    setIsConfirmationModal(false);
    updatePropertyStatus(
      propertyId,
      () => getAllProperties(getUpdatedPayload()),
      { isConfirm: true }
    );
  };

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  useEffect(() => {
    return () => {
      resetFilter();
    };
  }, []);

  useEffect(() => {
    getAllProperties(getUpdatedPayload());
    // eslint-disable-next-line
  }, [
    allPropertiesPagination.search,
    allPropertiesPagination.orderBy,
    allPropertiesPagination.pageSize,
    allPropertiesPagination.country,
  ]);

  return (
    <MainContentWrapper>
      {(spinner(adminReduxConstants.GET_AGENT_PROPERTIES) > -1 ||
        spinner(propertyConstants.ALL_PROPERTIES) > -1) && (
        <MainSpinner className="admin-spinner" />
      )}
      <ConfirmationModal
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => onConfirmation()}
        show={showConfirmationModal}
        message={CONFIRMATION_MESSAGE}
      />
      <ConfirmationModal
        onCancel={() => setIsConfirmationModal(false)}
        onConfirm={() => onReConfirmation()}
        show={isConfirmationModal}
        message={PROPERTY_LIMIT_EXCEEDED_MESSAGE}
      />
      <div className="admin-page-heading">Properties</div>
      <PropertyFilter
        allCountries={allCountries}
        searchValue={allPropertiesPagination.search}
        countryValue={allPropertiesPagination.country}
        orderByValue={allPropertiesPagination.orderBy}
        onResetFilters={resetFilter}
        onFilterChange={handleUpdatePagination}
        onCountryChange={handleChangeCountryFilter}
        searchPlaceHolder="Search by name (property or agent)"
      />
      <div className="row mt-3">
        {allProperties.length > 0 ? (
          allProperties.map((property) => (
            <div
              className="col-12 col-sm-6 col-md-12 col-lg-6"
              key={property.id}
            >
              <PropertyCardAdmin
                property={property}
                updateStatus={updateStatus}
              />
            </div>
          ))
        ) : (
          <div className="text-center mt-5 pt-5">No result found</div>
        )}
        {(allPropertiesPagination.total ?? 0) > allProperties.length && (
          <div className="w-100 d-flex justify-content-center pt-5">
            <RoundedButton
              className="px-5 py-2"
              onClick={() => {
                handleUpdatePagination(
                  allPropertiesPagination.pageSize + 10,
                  PAGE_SIZE
                );
              }}
            >
              View More
            </RoundedButton>
          </div>
        )}
      </div>
    </MainContentWrapper>
  );
}

const mapStateToProps = (state) => ({
  propertyId: state.propertyReducer.propertyId,
  allProperties: state.propertyReducer.allProperties,
  allPropertiesPagination: state.propertyReducer.allPropertiesPagination,
  spinnerActions: state.systemReducer.spinnerActions,
  isConfirmationModal: state.systemReducer.isConfirmationModal,
  allCountries: state.countryReducer.allCountries,
  selectedRowId: state.systemReducer.selectedRowId,
});

const mapDispatchToProps = (dispatch) => ({
  getAllProperties: (data) => dispatch(propertyActions.getAllProperties(data)),
  updatePropertyStatus: (id, func, body) =>
    dispatch(adminActions.updatePropertyStatus(id, func, body)),
  updatePagination: (data, type) =>
    dispatch(adminActions.updatePagination(data, type)),
  setIsConfirmationModal: (flag) =>
    dispatch(systemActions.setIsConfirmationModal(flag)),
  setSelectedRowId: (id) => dispatch(systemActions.setSelectedRowId(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminProperties);
